import { useState, useCallback, useEffect } from 'react'
import { UserPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import { getNextOrder } from '../../../../../baseComponents/TwinTable/functions'
import { InputTableURLsTable } from '../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { DefaultOrder, TableConfiguration } from '../../../../../baseComponents/TwinTable/types'
import { twinFetchPostJSON } from '../../../../../utils/globals/data'
import { OrderData } from '../types'
import { ConflictsTyComplex, ConflictsTyTmp } from '../../../../../baseComponents/ModalsLayouts/ModalOrderIncompatibles/types'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { addErrorsOfTableDataInHeader, buildOrderFieldsOrderLayout, cleanTableDataForOriginalTable, groupTableDataByAddonParent, groupTableDataByVariantParent, parseOrderTags, parseScalesOfOrderData } from '../useOrderScreenLogic/functions'
import { addMultiHeaderLine, addNLineMultiToOrderData, checkBTOIncompatibles, fillHeaderAfter, fillHeaderForConflicts, mergeShipping, parserConflicts } from './functions'
import useInputTableFilterData from '../../../../../utils/hooks/orders/useInputTableFilterData'

interface MultiOrderScreenLogicProps {
    inputTableURLs: InputTableURLsTable
    userPermissions: UserPermissions
    defaultOrder?: DefaultOrder
    type: 'buyHeader' | 'sellOrderHeader' | 'sellDeliveryNoteHeader'
    billingType: 'BillingTypeBuyOptions' | 'BillingTypeSellOptions'
}

const useMultiOrderScreenLogic = ({ inputTableURLs, defaultOrder, type, billingType }: MultiOrderScreenLogicProps) => {
    const [completeData, setCompleteData] = useState<OrderData | null>(null)
    const [conflicts, setConflicts] = useState<ConflictsTyComplex>({ incompatibles: {}, conflicts: [] })
    const [tableConfiguration, setTableConfiguration] = useState<TableConfiguration>({
        generalSearchbar: '',
        order: {},
        offset: 0,
        singleFieldsSearch: {},
        petitionNumber: 0
    })
    const { parseTableData } = useInputTableFilterData({ setCompleteData })

    const changeOrder = useCallback((fieldName: string) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            const newOrder = getNextOrder(fieldName, copyOld.order, defaultOrder?.order || 'desc')
            return {
                ...copyOld,
                offset: 0, 
                order: {...newOrder}
            }
        })
    }, [setTableConfiguration, defaultOrder?.order])

    const changeSingleField = useCallback((fieldName: string, value: string | string[]) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            return {
                ...copyOld,
                offset: 0,
                singleFieldsSearch: {
                    ...copyOld.singleFieldsSearch,
                    [fieldName]: value
                }
            }
        })
    }, [setTableConfiguration])
    
    const myTableConfigurationStr = JSON.stringify(tableConfiguration)
    const getDataStr = JSON.stringify(inputTableURLs.getData)
    const typeStr = JSON.stringify(type)
    const getCompleteData = useCallback(async () => {
        const myTableConfigurationParsed = JSON.parse(myTableConfigurationStr)
        const getDataParsed = JSON.parse(getDataStr)
        const typeParsed = JSON.parse(typeStr)
        const order = buildOrderFieldsOrderLayout(myTableConfigurationParsed.order)
        let orderData: OrderData = { header: {}, tableData: [], originalTableData: [], dictPosTableData: {}, filters: {
            generalSearchbar: '',
            order: {},
            singleFieldsSearch: {},
        }}
        let result = await twinFetchPostJSON(getDataParsed[0], { ...getDataParsed[1], includeRelations: true, order })
        let myConflicts: ConflictsTyTmp = {}
        let header: TwinDictionary = {}
        let OrderShipping: TwinDictionary = {}
        let full_assigned_number_parsed: string[] = []
        let incompatibles: TwinDictionary = {}
        const incompatiblesBTO = checkBTOIncompatibles(result, billingType)
        if (incompatiblesBTO) {
            incompatibles[billingType] = incompatiblesBTO
        }
        let nLine = 1
        for (let myOrder of result) {
            header = fillHeaderForConflicts(myOrder.header)
            const subConflicts = parserConflicts(header, orderData.header)
            if (myOrder.header.OrderShipping){
                OrderShipping = mergeShipping(OrderShipping || {}, myOrder.header.OrderShipping )
            }
            if (!OrderShipping.ShippingServiceId && myOrder.header.OrderShipping.ShippingServiceId){
                OrderShipping.ShippingServiceId = myOrder.header.OrderShipping.ShippingServiceId
                OrderShipping.ShippingService = myOrder.header.OrderShipping.ShippingService
            }
            if (myOrder?.header?.full_assigned_number){
                full_assigned_number_parsed.push(myOrder.header.full_assigned_number)
            }
            header = { ...header, ...fillHeaderAfter(myOrder.header) }
            if (!Object.keys(subConflicts).length) {
                orderData.header = { ...header, ...getDataParsed[1]}
            } else {
                myConflicts = { ...myConflicts, ...subConflicts }
            }
            if (myOrder['tableData']?.length) {
                myOrder.originalTableData = cleanTableDataForOriginalTable(myOrder.tableData)
                orderData.originalTableData = [...orderData.originalTableData, ...myOrder.originalTableData]
                myOrder.tableData = addMultiHeaderLine(myOrder.tableData, myOrder, typeParsed)
                myOrder.tableData = groupTableDataByVariantParent(myOrder.tableData)
                myOrder.tableData = groupTableDataByAddonParent(myOrder.tableData)
                myOrder.header = parseOrderTags(myOrder.header)
                myOrder = addErrorsOfTableDataInHeader(myOrder)
                const { myOrderData, position } = addNLineMultiToOrderData(myOrder, nLine)
                myOrder = myOrderData
                nLine = position
                myOrder = parseScalesOfOrderData(myOrder)
                orderData.tableData = [...orderData.tableData, ...myOrder.tableData]
            }
        }
        orderData.header.OrderShipping = OrderShipping
        orderData.header.full_assigned_number_parsed = full_assigned_number_parsed
        setConflicts({ incompatibles: incompatibles || {}, conflicts: [...Object.keys(myConflicts)] })
        let processedTableLength = 0
        orderData.dictPosTableData = {}
        for (const key in orderData.tableData) {
            const element = orderData.tableData[key]
            if(element.processed){
                processedTableLength++
            }
            const myParsedKey = parseInt(key)
            element.absoluteIndex = myParsedKey
            orderData.dictPosTableData[element.id] = myParsedKey
        }
        orderData.processedTableLength = processedTableLength
        return parseTableData((old) => old, orderData)
    }, [getDataStr, myTableConfigurationStr, parseTableData, setConflicts, typeStr, billingType])
    
    useEffect(() => {
        getCompleteData()
    }, [getCompleteData])

    return { completeData, conflicts, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, headerChanges: { changeOrder, changeSingleField },  setConflicts}
}

export default useMultiOrderScreenLogic